import { CREATE_NOTICE } from "../../../routes";
import { NOTICE_TITLE_MAX } from "../../notices/noticeRules";
import type { WizardHandover, WizardInitialFormData } from "../../notices/wizardSession";
import type { DayKey } from "../taskDetails";
import type { Recipient } from "./recipients";
import {
  CALLED_WHO,
  CHOOSE_IT_THERE,
  dayAlone,
  messageDetail,
  namesRecipient,
  preview,
  readDay,
  readRecipient,
  recipientInstruction,
  spotDay,
  spotRecipient,
  titleDetail,
  typed,
  whoQuestion as sharedWhoQuestion,
  withoutEndPunctuation,
} from "./sharedDetails";
import type { TaskContext, Question, TaskSchema } from "./taskConversation";

type NoticeDetails = {
  title?: string;
  message?: string;
  to?: Recipient;
  urgent?: boolean;
  /** Read only to strip a day out of the title or message; a Notice has nowhere in the wizard to show it */
  day?: DayKey;
};

/** Who it goes to, with Everyone (if they may) and their Circles to tap. */
const whoQuestion = (org: TaskContext) => sharedWhoQuestion("Who should it go to?", org.everyoneAndCircles);

/** Everyone they may send to, by name. */
const recipientsOf = (org: TaskContext) => [...org.everyoneAndCircles, ...org.users];

/** "make it urgent", "it's urgent", or just "Urgent". */
const URGENT_ALONE = /^(?:(?:please|and|also|oh|ok(?:ay)?)[\s,]+)*(?:(?:make|mark)\s+it(?:\s+as)?\s+|(?:it|this)(?:'s|\s+is)\s+)?urgent(?:\s+actually)?(?:\s+(?:please|pls))?[\s.!]*$/i;
/** "not urgent", "not an urgent matter", "nothing urgent" */
const NOT_URGENT = /\b(?:not|isn't|no|non|nothing)\b[\w\s-]{0,15}?\burgent\b/i;
/** The word Urgent and what says it in an answer: "This is urgent, …", "Urgent: …". */
const URGENT_WORDS = /[,\s]*\b(?:(?:this|it)(?:'s|\s+is)\s+)?urgent\b[:,\s-]*/i;

const DAY_QUESTION: Question = { text: "What's the date?", choices: [] };

/**
 * A Notice: a title, a message and who it goes to, asked in that order; Urgent only when
 * said. A Notice (Information or Urgent) has no date field in the wizard, unlike an Event,
 * so a day is never shown on the summary or carried into the handover. That's why a day
 * stays in the title and message as written: taken out, "Office closed Monday" would lose it.
 */
/** "something", "a thing": words that stand in for a topic that hasn't been said. */
const VAGUE_TOPIC = /^(?:something|anything|stuff|a\s+thing|some\s+things?|it|this|that|something\s+(?:now|quickly|urgent(?:ly)?))[.!]*$/i;

export const noticeTask: TaskSchema = {
  task: "notice",
  details: [
    // "Sports day on Friday for Grade 3" is "Sports day on Friday", to Grade 3
    titleDetail("Notice", ["to", "urgent"]),
    // Who it goes to only from "send it to …": a message names Circles for other reasons
    messageDetail(["day", "urgent"]),
    {
      name: "to",
      required: true,
      called: CALLED_WHO,
      // Who it goes to is chosen on Review
      step: "review",
      question: (org) => whoQuestion(org)(),
      read: (said, org) => readRecipient(said, recipientsOf(org), org, whoQuestion(org)),
      spot: (said, org) => spotRecipient(said, recipientsOf(org)),
      instruction: (said, org) => recipientInstruction(said, recipientsOf(org), org),
      alsoReads: ["day", "urgent"],
      isValue: (said, org) => namesRecipient(said, recipientsOf(org), org),
      writtenThere: CHOOSE_IT_THERE,
      requiresExplicitSkip: true,
    },
    {
      name: "urgent",
      called: /^(?:urgent|urgency)$/i,
      read: () => ({ value: true }),
      spot: (said) =>
        /\burgent\b/i.test(said) && !NOT_URGENT.test(said) ? { value: true, rest: said.replace(URGENT_WORDS, " ").trim() } : undefined,
      instruction: (said) => (URGENT_ALONE.test(typed(said)) ? "urgent" : undefined),
      isValue: (said) => /^urgent$/i.test(withoutEndPunctuation(said)),
    },
    {
      name: "day",
      called: /^(?:date|day)$/i,
      step: "review",
      question: () => DAY_QUESTION,
      read: readDay,
      spot: spotDay,
      instruction: (said, org) => dayAlone(said, org.today),
      isValue: (said, org) => Boolean(dayAlone(said, org.today)),
    },
  ],
  cannotStart: (org) =>
    org.everyoneAndCircles.length + org.users.length === 0
      ? "You don't have any Circles to send a Notice to yet."
      : undefined,
  // What it's about is the title, unless it's too long to be one: then it's what the Notice says
  saidIn: (said) => ({
    // "tell parents something": no topic yet, so the title is asked for rather than called "Something"
    ...(said.topic && VAGUE_TOPIC.test(said.topic.trim())
      ? {}
      : said.topic && said.topic.length > NOTICE_TITLE_MAX
        ? { message: said.topic }
        : { title: said.topic }),
    // "saying …", "titled X saying …": the words to send, as given
    ...(said.message ? { message: said.message } : {}),
    to: said.audience,
    urgent: said.noticeType === "urgent" ? "urgent" : undefined,
    day: said.day,
  }),
  summary: (details, org) => {
    const { title, message, to, urgent } = details as NoticeDetails;
    return [
      ...(urgent && to?.kind !== "user" ? [{ label: "Type", value: "Urgent" }] : []),
      ...(title ? [{ label: "Title", value: title }] : []),
      { label: "To", value: to?.name ?? "choose on the next screen" },
      // A Notice has no date field in the wizard: the day stays in the title or message, never its own row
      ...(message ? [{ label: "Message", value: preview(message) }] : []),
    ];
  },
  handover: (details, org, { startAt = "review" }) => {
    const { title = "", message = "", to, urgent } = details as NoticeDetails;
    const common: WizardInitialFormData = {
      title,
      // As typed: the wizard shows plain text as it is, or turns it into paragraphs when formatting is on
      body: message,
    };
    // A Notice to one User is always a plain one
    const prefillNotice: WizardHandover["prefillNotice"] =
      to?.kind === "user"
        ? { noticeType: "direct", startAt, initialFormData: { ...common, targetUserId: to.id } }
        : {
            noticeType: urgent ? "urgent" : "info",
            startAt,
            initialFormData: {
              ...common,
              // Not known yet, it's chosen in the wizard rather than guessed (an Urgent Notice would start on Everyone)
              audience: to?.kind === "circle" ? "subcircle" : to?.kind === "everyone" ? "circle" : "",
              ...(to?.kind === "circle" ? { targetSubcircle: to.id } : {}),
            },
          };
    const state: WizardHandover = { prefillNotice };
    return { pathname: CREATE_NOTICE, state };
  },
  readyText: "Here's your Notice. Nothing is sent until you press Publish.",
  handedOverText: "Here it is. Nothing is sent until you press Publish.",
  fillInThereText: "No problem, I'll open the Notice so you can fill that in there.",
};
